import { union } from 'lodash/array'
import { orderBy } from 'lodash/collection'
import { isArray } from 'lodash/lang'

import Vue from 'vue'
import Vuex from 'vuex'
import { axios, EQ_OPERATOR } from '@argon/app/axios'
import { stampItem } from '@argon/app/helpers'

export const NAMESPACE = 'app'

const state = {
  title: '',
  isLeftDrawerOpened: true,
  isRightDrawerOpened: false,
  launcherItems: [],
  headerItems: [],
  notificationItems: [],
  currentNav: 'default',
  navs: {
    default: []
  },
  filters: [],
  appliedFilters: {},
  contextObj: null,
  snackMessage: null,
  offline: false,
  isMultiOrg: process.env.VUE_APP_ARGON_MULTI_ORG ? process.env.VUE_APP_ARGON_MULTI_ORG === 'true' : false
}

const getters = {
  sortedLauncherItems: (state) => {
    return orderBy(state.launcherItems, ['weight', 'name'], ['asc', 'asc'])
  },
  sortedHeaderItems: (state) => {
    return orderBy(state.headerItems, ['weight', 'name'], ['asc', 'asc']).reverse()
  },
  currentNavItems: (state) => {
    return [...state.navs['default'], ...state.navs[state.currentNav]]
  },
  sortedCurrentNavItems: (state) => {
    const navItems = [...state.navs['default'], ...state.navs[state.currentNav]]
    return orderBy(navItems, ['weight', 'title'], ['asc', 'asc'])
  },
  offline: (state) => state.offline,
  isMultiOrg: (state) => state.isMultiOrg,
  contextType: (state) => (state.contextObj ? state.contextObj.type : null),
  contextRef: (state) => (state.contextObj ? state.contextObj.refname : null),
  contextAvatar: (state) => (state.contextObj ? state.contextObj.avatar : null),
  contextDisplay: (state) => (state.contextObj ? state.contextObj.display : null)
}

const mutations = {
  SET_FILTERS: (state, value) => {
    state.filters = value
  },
  SET_APPLIED_FILTERS: (state, value) => {
    state.appliedFilters = value
  },
  SET_LEFT_DRAWER_OPENED: (state, value) => {
    state.isLeftDrawerOpened = value
  },
  TOGGLE_LEFT_DRAWER_OPENED: (state) => {
    state.isLeftDrawerOpened = !state.isLeftDrawerOpened
  },
  SET_RIGHT_DRAWER_OPENED: (state, value) => {
    state.isRightDrawerOpened = value
  },
  TOGGLE_RIGHT_DRAWER_OPENED: (state) => {
    state.isRightDrawerOpened = !state.isRightDrawerOpened
  },
  SET_TITLE: (state, value) => {
    state.title = value
  },
  SET_SNACKBAR: (state, value) => {
    state.snackMessage = value
  },
  SET_OFFLINE: (state, value) => {
    state.offline = value
  },
  ADD_LAUNCHER_ITEMS: (state, value) => {
    state.launcherItems = union(state.launcherItems, value)
  },
  ADD_HEADER_ITEMS: (state, value) => {
    state.headerItems = union(state.headerItems, value)
  },
  SET_NOTIFICATION_ITEMS: (state, value) => {
    state.notificationItems = value
  },
  ADD_NAV_ITEMS: (state, value) => {
    for (let nav in value) {
      state.navs[nav] = union(state.navs[nav], value[nav])
    }
  },
  SET_CURRENT_NAV: (state, value) => {
    state.currentNav = value
  },
  SET_CONTEXT_OBJECT: (state, value) => {
    state.contextObj = value
  }
}

const actions = {
  setLeftDrawerOpened: (context, value) => {
    context.commit('SET_LEFT_DRAWER_OPENED', value)
  },
  toggleLeftDrawerOpened: (context) => {
    context.commit('TOGGLE_LEFT_DRAWER_OPENED')
  },
  setRightDrawerOpened: (context, value) => {
    context.commit('SET_RIGHT_DRAWER_OPENED', value)
  },
  toggleRightDrawerOpened: (context) => {
    context.commit('TOGGLE_RIGHT_DRAWER_OPENED')
  },
  setTitle: (context, value) => {
    context.commit('SET_TITLE', value)
  },
  setSnackMessage: ({ commit }, value) => {
    commit('SET_SNACKBAR', value)
  },
  addLauncherItems: (context, value) => {
    context.commit('ADD_LAUNCHER_ITEMS', value)
  },
  addHeaderItems: (context, value) => {
    context.commit('ADD_HEADER_ITEMS', value)
  },
  addNotification: ({ commit, state }, value) => {
    commit('SET_NOTIFICATION_ITEMS', [...state.notificationItems, value])
  },
  removeNotificationByName: ({ commit, state }, name) => {
    commit(
      'SET_NOTIFICATION_ITEMS',
      state.notificationItems.filter((x) => x.name !== name)
    )
  },
  removeNotificationsByNamespace: ({ commit, state }, name) => {
    commit(
      'SET_NOTIFICATION_ITEMS',
      state.notificationItems.filter((x) => x.namespace !== name)
    )
  },
  replaceNotificationsByNamespace: ({ commit, state }, payload) => {
    const { name, value } = payload
    let filtered = state.notificationItems.filter((x) => x.namespace !== name)
    let updated = [...filtered, value]
    commit('SET_NOTIFICATION_ITEMS', updated)
  },
  replaceNotificationsByName: ({ commit, state }, payload) => {
    let filtered = state.notificationItems.filter((x) => x.name !== payload.name)
    let updated = [...filtered, payload]
    commit('SET_NOTIFICATION_ITEMS', updated)
  },
  addNavItems: (context, value) => {
    let navItems = {}
    if (isArray(value)) {
      navItems['default'] = value
    } else {
      navItems = value
    }
    context.commit('ADD_NAV_ITEMS', navItems)
  },
  setCurrentNav: (context, value) => {
    context.commit('SET_CURRENT_NAV', value)
  },
  setOffline: (context, value) => {
    context.commit('SET_OFFLINE', value)
  },
  setContextObj: ({ commit, state }, value) => {
    return new Promise((resolve, reject) => {
      if (!state.contextObj || value.id !== state.contextObj.id || value.refname !== state.contextObj.refname) {
        commit('SET_CONTEXT_OBJECT', value)
      }
      resolve(true)
    })
  },
  addFile: ({}, formData) => {
    return new Promise((resolve, reject) => {
      axios({ url: '/files', data: formData, method: 'POST' })
        .then(({ data }) => {
          resolve(data)
        })
        .catch(reject)
    })
  },
  addFiles: ({ dispatch }, formDatas) => {
    const promises = formDatas.map((formData) => dispatch('addFile', formData))
    return new Promise((resolve, reject) => {
      Promise.all(promises).then(resolve).catch(reject)
    })
  },
  deleteFile: ({}, fileId) => {
    return new Promise((resolve, reject) => {
      axios({ url: `/files/${fileId}`, method: 'DELETE' }).then(() => {
        resolve(true)
      })
    })
  },
  deleteFiles: ({ dispatch }, fileIds) => {
    const promises = fileIds.map((fileId) => dispatch('deleteFile', fileId))
    return new Promise((resolve, reject) => {
      Promise.all(promises).then(resolve).catch(reject)
    })
  },
  listSavedFilters: ({ commit }, context) => {
    return new Promise((resolve, reject) => {
      axios({ url: '/filters', params: { filters: `context:${EQ_OPERATOR}:${context}` }, method: 'GET' })
        .then(({ data }) => {
          commit('SET_FILTERS', data.items)
          resolve(data.items)
        })
        .catch(reject)
    })
  },
  setAppliedFilter: ({ commit }, { context, filterObject }) => {
    commit('SET_APPLIED_FILTERS', { ...state.appliedFilters, [context]: stampItem(filterObject) })
  },
  clearAppliedFilter: ({ commit }, context) => {
    const applied = { ...state.appliedFilters }
    delete applied[context]
    commit('SET_APPLIED_FILTERS', applied)
  },
  saveAppliedFilter: ({ dispatch, state }, { context, name }) => {
    return new Promise((resolve, reject) => {
      const meta = state.appliedFilters[context]
      if (!meta) resolve(true)
      delete meta['stamp']
      const data = { name, meta, context }
      axios({ url: '/filters', method: 'POST', data })
        .then(() => {
          return dispatch('listSavedFilters', context)
        })
        .then(() => {
          resolve(true)
        })
        .catch(reject)
    })
  },
  deleteSavedFilter: ({ dispatch }, { filterId, context }) => {
    return new Promise((resolve, reject) => {
      axios({ url: `/filters/${filterId}`, method: 'DELETE' })
        .then(() => {
          return dispatch('listSavedFilters', context)
        })
        .then(() => {
          resolve(true)
        })
        .catch(reject)
    })
  }
}

Vue.use(Vuex)

export const store = new Vuex.Store({
  modules: {
    app: {
      namespaced: true,
      state: state,
      getters: getters,
      mutations: mutations,
      actions: actions
    }
  }
})
